import React, { useEffect, useRef, useState } from 'react';
import Spinner from '../Spinner';
import type { IProjects } from '../../stores/projects';
import { shouldLoadProjects } from './AppLoader.utils';

type AppLoaderProjects = IProjects;

/** Props for {@link AppLoader}. */
export interface AppLoaderProps {
  /** The projects store to initialize. */
  projects: AppLoaderProjects;
}

export default function AppLoader({ projects }: AppLoaderProps) {
  const [error, setError] = useState(false);
  const loadingRef = useRef(false);

  const load = () => {
    if (!shouldLoadProjects(projects, loadingRef.current)) {
      return;
    }

    loadingRef.current = true;
    projects.initialize()
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        loadingRef.current = false;
      });
  };

  // Kick off the load once on mount (was componentDidMount).
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderError = () => {
    return (
      <div className="loading-error flex h-full items-center justify-center text-center">
        We're sorry, we couldn't load your models.
        <button
          type="button"
          onClick={() => load()}
          className="ml-2.5 cursor-pointer rounded-md border border-grey bg-white px-2.5 py-1.5 text-black hover:bg-light focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          Retry
        </button>
      </div>
    );
  };

  const renderLoading = () => {
    return <Spinner />;
  };

  return error ? renderError() : renderLoading();
}
